import React, { useState, useCallback, useMemo } from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { saveToLocalStorage } from '@/shared/localStorage/localStorageRepository'
import {
    IS_COOKIES_ACCEPTED,
    defaultAnalyticsConsent,
    grantedAnalyticsConsent,
} from '@/shared/constants'
import { useAppStore } from '@/shared/store'
import { CloseIcon } from '@/shared/ui/Icons'
import { Link } from 'react-router-dom'

const wrapperStyles: React.CSSProperties = {
    background: 'transparent',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: 1.15,
    textRendering: 'optimizeLegibility',
    position: 'fixed',
    zIndex: 1000,
}

const closeButtonStyles: React.CSSProperties = {
    borderRadius: 0,
    borderBottomLeftRadius: '0.4rem',
    border: '1px solid white',
    borderTop: 'none',
    borderRight: 'none',
    fontSize: '1em',
    height: '42px',
    width: '42px',
    padding: 0,
    position: 'absolute',
    right: 0,
    top: 0,
}

const buttonStyles: React.CSSProperties = {
    padding: '8px 12px',
    gap: '8px',
    borderRadius: '10px',
}

interface ModalLoaderProps {
    open: boolean
    handleClose: () => void
}

const CookiesModal: React.FC<ModalLoaderProps> = ({ open, handleClose }) => {
    const { t: translate } = useTranslation()
    const theme = useTheme()
    const { setIsCookiesAccepted, setAnalyticsConfigConsent, tAndC } =
        useAppStore()
    const [isClosing, setIsClosing] = useState(false)

    const modalWrapperStyles = useMemo(
        () => ({
            opacity: open && !isClosing ? 1 : 0,
            transform:
                open && !isClosing ? 'translateY(0)' : 'translateY(20px)',
            visibility: open || isClosing ? 'visible' : 'hidden',
            transition: 'opacity 0.5s ease, transform 0.5s ease',
            marginLeft: '1rem',
            right: '1rem',
            bottom: '1rem',
            boxShadow: '0 0.625em 1.875em rgba(0, 0, 2, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '36em',
            position: 'fixed',
            zIndex: 1,
        }),
        [open, isClosing]
    )

    const startClosing = useCallback(() => {
        setIsClosing(true)
        setTimeout(() => {
            handleClose()
            setIsClosing(false)
        }, 500)
    }, [handleClose])

    const handleDeclineCookies = useCallback(() => {
        setIsCookiesAccepted(false)
        setAnalyticsConfigConsent(defaultAnalyticsConsent)
        saveToLocalStorage(IS_COOKIES_ACCEPTED, 'false')
        startClosing()
    }, [setIsCookiesAccepted, setAnalyticsConfigConsent, startClosing])

    const handleAcceptCookies = useCallback(() => {
        setIsCookiesAccepted(true)
        setAnalyticsConfigConsent(grantedAnalyticsConsent)
        saveToLocalStorage(IS_COOKIES_ACCEPTED, 'true')
        startClosing()
    }, [setIsCookiesAccepted, setAnalyticsConfigConsent, startClosing])

    return (
        <Box sx={wrapperStyles} onClick={startClosing}>
            <Box
                sx={{
                    ...modalWrapperStyles,
                    backgroundColor: theme.palette.custom.neutralDark,
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        justifyContent: 'space-between',
                    }}
                    onClick={(event) => event.stopPropagation()}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            padding: '1rem 0 0',
                            gap: '20px',
                        }}>
                        <Typography
                            sx={{
                                padding: '0 1.3rem',
                                paddingRight: '3rem',
                                fontSize: '1.05em',
                                fontWeight: 600,
                            }}>
                            {translate('cookiesHeader')}
                        </Typography>
                        <Typography
                            sx={{
                                padding: '0 1.3rem',
                                color: theme.palette.custom.lightGray,
                                fontSize: '0.9em',
                                lineHeight: 1.5,
                                maxHeight: '40vh',
                                overflowY: 'auto',
                                paddingBottom: '1em',
                            }}>
                            {translate('cookies')}{' '}
                            <Link
                                to={tAndC}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: theme.palette.primary.main }}>
                                {translate('privacyPolicyCookie')}
                            </Link>
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '20px',
                            padding: '1rem 1.3rem',
                            borderTop: `1px solid ${theme.palette.custom.border}`,
                        }}>
                        <Button
                            data-testid="cancel-button"
                            sx={{
                                ...buttonStyles,
                                border: '1px solid white',
                                backgroundColor: 'transparent',
                                color: 'white',
                            }}
                            variant="outlined"
                            onClick={handleDeclineCookies}>
                            <Typography color="white" variant="label4">
                                {translate('decline')}
                            </Typography>
                        </Button>
                        <Button
                            data-testid="accept-button"
                            sx={{
                                ...buttonStyles,
                                backgroundColor: theme.palette.primary.main,
                            }}
                            onClick={handleAcceptCookies}>
                            <Typography color="white" variant="label4">
                                {translate('accepting')}
                            </Typography>
                        </Button>
                    </Box>
                    <Button sx={closeButtonStyles} onClick={startClosing}>
                        <CloseIcon />
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}

export default CookiesModal
